import { useConversationList } from '@modules/ai-conversation/hooks/useConversationList';
import {
  IconButton,
  ListItemText,
  MenuItem,
  TextField,
  alpha,
  styled,
} from '@mui/material';
import { bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { AiChat } from 'src/@types/ai';
import { PATH_DASHBOARD } from 'src/Router/paths';
import Iconify from 'src/components/iconify';
import { useConversationDeleteMutation } from '../queries/useConversationDeleteMutation';
import { useConversationUpdateMutation } from '../queries/useConversationUpdateMutation';
import { ConversationAiItemOptions } from './ConversationAiItemOptions';

interface ConversationAiItemProps {
  chat: AiChat;
  shouldNavigate?: boolean;
}

export const ConversationAiItem = ({
  chat,
  shouldNavigate,
}: ConversationAiItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(chat.title);
  const [isHovered, setIsHovered] = useState(false);

  const navigate = useNavigate();

  const {
    currentConversationId,
    setCurrentConversationId,
    conversationList,
    setConversation,
    setInputControl,
  } = useConversationList();

  const { deleteConversation } = useConversationDeleteMutation();
  const { updateConversation } = useConversationUpdateMutation();

  const optionState = usePopupState({
    variant: 'popover',
    popupId: 'demoPopover',
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      updateConversation({ conversationId: chat._id, title });
      setIsEditing(false);
    }
  };

  const isSelected = currentConversationId === chat._id;

  const { onClick: triggerOnClick } = bindTrigger(optionState);

  const handleOpenChat = (chat: AiChat) => {
    if (isSelected) return;
    if (shouldNavigate) {
      navigate(PATH_DASHBOARD.ai.view(chat._id));
    } else {
      if (!conversationList[chat._id]) {
        setConversation(chat);
        setInputControl(chat._id, true);
      }
      setCurrentConversationId(chat._id);
    }
  };

  return (
    <StyledMenuItem
      key={chat._id}
      selected={isSelected}
      onClick={() => handleOpenChat(chat)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isEditing ? (
        <TextField
          fullWidth
          value={title}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          variant="outlined"
          size="small"
          autoFocus
        />
      ) : (
        <ListItemText
          primaryTypographyProps={{
            noWrap: true,
          }}
        >
          {chat.title}
        </ListItemText>
      )}
      {!isEditing && (isSelected || isHovered) && (
        <>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              triggerOnClick(e);
            }}
          >
            <Iconify icon="tabler:dots" width={16} height={16} />
          </IconButton>

          <ConversationAiItemOptions
            state={optionState}
            onDelete={() => deleteConversation(chat._id)}
            onEdit={handleEdit}
          />
        </>
      )}
    </StyledMenuItem>
  );
};

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  alignItems: 'center',
  height: theme.spacing(4),
  gap: theme.spacing(0.5),

  '&.MuiMenuItem-root': {
    marginBottom: 0,
    lineHeight: theme.spacing(3),
    padding: theme.spacing(0, 1.5),
    '& .MuiTypography-root': {
      ...theme.typography.body2,
    },
  },
  '&.Mui-selected': {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.activatedOpacity,
    ),
    color: theme.palette.primary.main,
  },
}));
